/**
 * Formatação centralizada da duração de um evento/cardápio.
 *
 * Alguns fornecedores atendem um tempo FIXO ("4 horas") e outros trabalham com
 * um teto ("até 4 horas"). A flag `isMax` (persistida em
 * `supplier_menu.event_hour_is_max` e congelada em `events.event_hour_is_max`)
 * altera APENAS a comunicação: cálculos de término, agenda e hora extra
 * continuam usando o valor numérico original.
 */

/** Converte valores heterogêneos (number, "4", "04:00", "04:00:00") em horas decimais. */
export const parseDurationToHours = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null;

  if (typeof value === 'number') {
    return Number.isFinite(value) && value > 0 ? value : null;
  }

  const str = String(value).trim();
  if (!str) return null;

  if (str.includes(':')) {
    const [hh, mm] = str.split(':').map(Number);
    const hours = (Number.isFinite(hh) ? hh : 0) + (Number.isFinite(mm) ? mm : 0) / 60;
    return hours > 0 ? hours : null;
  }

  const numeric = Number(str.replace(',', '.'));
  return Number.isFinite(numeric) && numeric > 0 ? numeric : null;
};

/** Retorna "4" ou "4h30" a partir de horas decimais. */
const formatHoursNumber = (hours: number): string => {
  const wholeHours = Math.floor(hours);
  const minutes = Math.round((hours - wholeHours) * 60);
  if (minutes === 0) return String(wholeHours);
  return `${wholeHours}h${String(minutes).padStart(2, '0')}`;
};

export interface FormatEventDurationOptions {
  /** Sufixo aplicado quando a duração é um número inteiro de horas. Padrão: "horas". */
  unit?: 'horas' | 'h' | 'none';
}

/**
 * Formata a duração para exibição.
 *
 * @example formatEventDuration(4, false) // "4 horas"
 * @example formatEventDuration(4, true)  // "até 4 horas"
 * @example formatEventDuration(4, true, { unit: 'h' }) // "até 4h"
 */
export const formatEventDuration = (
  value: unknown,
  isMax?: boolean | null,
  options: FormatEventDurationOptions = {},
): string => {
  const hours = parseDurationToHours(value);
  if (hours === null) return '';

  const { unit = 'horas' } = options;
  const formatted = formatHoursNumber(hours);
  const hasMinutes = formatted.includes('h');

  let label: string;
  if (unit === 'none' || hasMinutes) {
    label = formatted;
  } else if (unit === 'h') {
    label = `${formatted}h`;
  } else {
    label = `${formatted} ${hours === 1 ? 'hora' : 'horas'}`;
  }

  return isMax ? `até ${label}` : label;
};

/** Prefixo isolado, útil para textos já montados ("término previsto"). */
export const maxDurationPrefix = (isMax?: boolean | null): string => (isMax ? 'até ' : '');

/**
 * Formata a antecedência de chegada da equipe (supplier_menu.hour_arrived /
 * events.supplier_arrival_time, formato "HH:MM") como "2 horas", "1h30" etc.
 */
export const formatArrivalLead = (value: unknown): string => {
  const hours = parseDurationToHours(value);
  if (hours === null) return '';
  return formatEventDuration(hours, false);
};

/**
 * Antecedência de chegada da equipe, a partir do HORÁRIO de início do evento e
 * do HORÁRIO de chegada (events.supplier_arrival_time, ex.: 10:30 para início
 * às 12:00 => "1h30"). Devolve '' quando algum dos horários é inválido.
 */
export const computeArrivalLeadLabel = (startTime: unknown, arrivalTime: unknown): string => {
  const toMinutes = (value: unknown): number | null => {
    const match = /^(\d{1,2}):(\d{2})/.exec(String(value ?? '').trim());
    if (!match) return null;
    return Number(match[1]) * 60 + Number(match[2]);
  };
  const start = toMinutes(startTime);
  const arrival = toMinutes(arrivalTime);
  if (start === null || arrival === null) return '';
  const diff = (start - arrival + 24 * 60) % (24 * 60);
  if (diff <= 0) return '';
  const hh = String(Math.floor(diff / 60)).padStart(2, '0');
  const mm = String(diff % 60).padStart(2, '0');
  return formatArrivalLead(`${hh}:${mm}`);
};
